import { lazy, Suspense, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSwipeable } from 'react-swipeable';
import { ActionIcon, Badge, Loader, Modal, Text } from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconDownload,
  IconX,
} from '@tabler/icons-react';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import { downloadAndSaveAttachment } from '@/utils/attachmentDownload';
import { getPreviewTier } from '@/utils/filePreviewUtils';
import { ICON_SIZE_MD } from '@/constants';
import { Z_INDEX_MOBILE_MODAL_DATEPICKER } from '@/constants/zIndex';
import type {
  FilePreviewModalProps,
  ViewerProps,
} from './FilePreviewModal.types';
import { ImageViewer, TextViewer, DownloadOnlyViewer } from './viewers';
import styles from './FilePreviewModal.module.css';

const LazyPdfViewer = lazy(() =>
  import('./viewers/PdfViewer').then((module) => ({
    default: module.PdfViewer,
  }))
);
const LazyCsvViewer = lazy(() =>
  import('./viewers/CsvViewer').then((module) => ({
    default: module.CsvViewer,
  }))
);
const LazyDocxViewer = lazy(() =>
  import('./viewers/DocxViewer').then((module) => ({
    default: module.DocxViewer,
  }))
);
const LazySpreadsheetViewer = lazy(() =>
  import('./viewers/SpreadsheetViewer').then((module) => ({
    default: module.SpreadsheetViewer,
  }))
);

const ViewerFallback = () => (
  <div className={styles.centeredMessage}>
    <Loader size="md" />
  </div>
);

export function FilePreviewModal({
  opened,
  onClose,
  currentIndex,
  totalCount,
  currentAttachment,
  data,
  loading,
  error,
  onNavigateNext,
  onNavigatePrev,
}: FilePreviewModalProps) {
  const { t } = useTranslation();
  const { isMobile } = useDeviceLayout();

  const tier = useMemo(
    () =>
      currentAttachment
        ? getPreviewTier(
            currentAttachment.contentType,
            currentAttachment.fileName
          )
        : 'download-only',
    [currentAttachment]
  );

  const canNavigatePrev = currentIndex > 0;
  const canNavigateNext = currentIndex < totalCount - 1;

  useEffect(() => {
    if (!opened) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && canNavigatePrev) {
        onNavigatePrev();
      } else if (event.key === 'ArrowRight' && canNavigateNext) {
        onNavigateNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    opened,
    canNavigatePrev,
    canNavigateNext,
    onNavigatePrev,
    onNavigateNext,
  ]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (canNavigateNext) {
        onNavigateNext();
      }
    },
    onSwipedRight: () => {
      if (canNavigatePrev) {
        onNavigatePrev();
      }
    },
    delta: 50,
    trackMouse: false,
  });

  const handleDownload = useCallback(async () => {
    if (!currentAttachment) {
      return;
    }
    try {
      await downloadAndSaveAttachment(currentAttachment, data);
    } catch (downloadError) {
      console.error('Failed to download attachment:', downloadError);
    }
  }, [currentAttachment, data]);

  const renderViewer = () => {
    if (loading) {
      return (
        <div className={styles.centeredMessage}>
          <Loader size="md" />
          <Text size="sm" c="dimmed">
            {t('filePreview.loading')}
          </Text>
        </div>
      );
    }

    if (error || !data || !currentAttachment) {
      return (
        <div className={styles.centeredMessage}>
          <Text c="red">{t('filePreview.error')}</Text>
        </div>
      );
    }

    const viewerProps: ViewerProps = {
      data,
      fileName: currentAttachment.fileName,
      contentType: currentAttachment.contentType,
    };
    const viewerKey = currentAttachment.id;

    switch (tier) {
      case 'image':
        return <ImageViewer key={viewerKey} {...viewerProps} />;
      case 'pdf':
        return (
          <Suspense fallback={<ViewerFallback />}>
            <LazyPdfViewer key={viewerKey} {...viewerProps} />
          </Suspense>
        );
      case 'text':
        return <TextViewer key={viewerKey} {...viewerProps} />;
      case 'csv':
        return (
          <Suspense fallback={<ViewerFallback />}>
            <LazyCsvViewer key={viewerKey} {...viewerProps} />
          </Suspense>
        );
      case 'docx':
        return (
          <Suspense fallback={<ViewerFallback />}>
            <LazyDocxViewer key={viewerKey} {...viewerProps} />
          </Suspense>
        );
      case 'spreadsheet':
        return (
          <Suspense fallback={<ViewerFallback />}>
            <LazySpreadsheetViewer key={viewerKey} {...viewerProps} />
          </Suspense>
        );
      default:
        return <DownloadOnlyViewer />;
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen={isMobile}
      size="xl"
      centered={!isMobile}
      radius={isMobile ? 0 : 'lg'}
      padding={0}
      withCloseButton={false}
      zIndex={Z_INDEX_MOBILE_MODAL_DATEPICKER}
      styles={{
        overlay: { zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER },
        content: {
          display: 'flex',
          flexDirection: 'column',
          height: isMobile ? '100dvh' : '90dvh',
          maxHeight: isMobile ? '100dvh' : '90dvh',
        },
        body: {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        },
      }}
      aria-label={currentAttachment?.fileName ?? ''}
    >
      <div className={styles.toolbar}>
        <Text className={styles.toolbarFileName} lineClamp={1}>
          {currentAttachment?.fileName}
        </Text>

        {totalCount > 1 && (
          <Badge variant="light" size="sm">
            {t('filePreview.itemOf', {
              current: currentIndex + 1,
              total: totalCount,
            })}
          </Badge>
        )}

        <div className={styles.toolbarSpacer} />

        <div className={styles.toolbarActions}>
          <ActionIcon
            variant="subtle"
            size="lg"
            radius="md"
            onClick={handleDownload}
            aria-label={t('filePreview.download')}
          >
            <IconDownload size={ICON_SIZE_MD} />
          </ActionIcon>

          <ActionIcon
            variant="subtle"
            size="lg"
            radius="md"
            onClick={onClose}
            aria-label={t('filePreview.close')}
          >
            <IconX size={ICON_SIZE_MD} />
          </ActionIcon>
        </div>
      </div>

      <div className={styles.contentArea} {...swipeHandlers}>
        {renderViewer()}

        {!isMobile && totalCount > 1 && (
          <div className={styles.galleryArrowsOverlay}>
            {canNavigatePrev && (
              <ActionIcon
                variant="filled"
                size="xl"
                radius="xl"
                className={`${styles.galleryArrow} ${styles.galleryArrowLeft}`}
                onClick={onNavigatePrev}
                aria-label={t('filePreview.previous')}
              >
                <IconChevronLeft size={ICON_SIZE_MD} />
              </ActionIcon>
            )}
            {canNavigateNext && (
              <ActionIcon
                variant="filled"
                size="xl"
                radius="xl"
                className={`${styles.galleryArrow} ${styles.galleryArrowRight}`}
                onClick={onNavigateNext}
                aria-label={t('filePreview.next')}
              >
                <IconChevronRight size={ICON_SIZE_MD} />
              </ActionIcon>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
